import { useEffect, useRef, useCallback } from "react";

const STORAGE_PREFIX = "scrollRestore_";

const getScrollContainer = (): HTMLElement | null =>
  document.querySelector("main.flex-1") as HTMLElement | null;

export function useScrollRestore(key: string, loading: boolean) {
  const storageKey = `${STORAGE_PREFIX}${key}`;
  const countKey = `${STORAGE_PREFIX}${key}_count`;
  const restored = useRef(false);

  // Restore once loading finishes for the first time
  useEffect(() => {
    if (loading || restored.current) return;
    const saved = sessionStorage.getItem(storageKey);
    if (saved) {
      const container = getScrollContainer();
      if (container) {
        // Delay to ensure all DOM elements are rendered
        setTimeout(() => {
          if (container) {
            container.scrollTop = Number(saved);
          }
        }, 20);
        sessionStorage.removeItem(storageKey);
      }
    }
    restored.current = true;
  }, [loading, storageKey]);

  // Call before navigating away
  const saveScroll = useCallback(() => {
    const container = getScrollContainer();
    if (container) {
      sessionStorage.setItem(storageKey, String(container.scrollTop));
    }
  }, [storageKey]);

  // Save item count for infinite scroll restoration
  const saveItemCount = useCallback(
    (count: number) => {
      sessionStorage.setItem(countKey, String(count));
    },
    [countKey]
  );

  const getSavedItemCount = useCallback((): number | null => {
    const saved = sessionStorage.getItem(countKey);
    if (saved) {
      sessionStorage.removeItem(countKey);
      return Number(saved);
    }
    return null;
  }, [countKey]);

  return { saveScroll, saveItemCount, getSavedItemCount };
}
